import { getAxiosClient } from './common';
import type { DownloadSignedUrlResponse } from './projects2';

const axios = getAxiosClient();

// Mirrors the server's SanitizedProjectCustomData (secrets stripped: capabilitiesUrl, resolvedTileTemplate).
export type CustomDataType = 'wmts' | 'shapefile';
export type CustomDataState = 'processing' | 'ready' | 'failed';

// Only a `ready` layer may be shown to customers; processing/failed layers are locked off.
export function isCustomDataReady(state: CustomDataState): boolean {
  return state === 'ready';
}

export interface ProjectCustomData {
  id: string;
  projectId: string;
  organizationId: string;
  type: CustomDataType;
  label: string;
  state: CustomDataState;
  visibleToCustomer: boolean;
  // Token-safe source (capabilitiesUrl with query string dropped); null for non-WMTS / unparseable.
  sourceUrl: string | null;
  // WMTS-only
  wmtsLayerIdentifier: string | null;
  tileMatrixSet: string | null;
  maxZoom: number | null;
  imageFormat: string | null;
  // shapefile-only
  s3Key: string | null;
  sourceFilename: string | null;
  featureCount: number | null;
  sourceCrs: string | null;
  // Distinct CateTitle categories + per-value counts; drives the categorical legend.
  // `geometryType` (set at ingest) selects the legend swatch shape; absent for pre-existing rows.
  // `color` is the uploader's pick; absent rows fall back to the palette derived from `value`.
  // `label` is the admin's chosen name; absent rows fall back to the name humanized from `value`.
  // Always present (server column is NOT NULL DEFAULT '[]'); empty for WMTS / pre-ingest rows.
  categories: {
    value: string;
    count: number;
    geometryType?: 'point' | 'line' | 'polygon';
    color?: string;
    label?: string;
  }[];
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AddWmtsCustomDataBody {
  capabilitiesUrl: string;
  layerIdentifier?: string;
  label?: string;
  visibleToCustomer: boolean;
}

// Mirrors the server's WmtsLayerOption/WmtsInspectResult (packages/api/src/common/wmts.ts) — keep in sync; no shared type boundary.
export interface WmtsLayerOption {
  identifier: string;
  title: string;
  suggestedLabel: string;
}

export interface WmtsInspectResult {
  layers: WmtsLayerOption[];
  hostname: string;
}

// Mirrors the server's multer cap (SHAPEFILE_UPLOAD_MAX_BYTES); kept in sync for fast client feedback.
export const SHAPEFILE_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

// Mirror the backend's `.zip`-extension + 50MB checks so users get instant feedback; the server stays
// the source of truth (zip mimetypes are unreliable, so we match its extension-only check).
export function validateShapefileFile(file: File): string | null {
  if (!file.name.toLowerCase().endsWith('.zip')) {
    return 'Only .zip shapefile archives are accepted.';
  }
  if (file.size > SHAPEFILE_UPLOAD_MAX_BYTES) {
    return 'File is too large (max 50MB).';
  }
  return null;
}

export async function listProjectCustomData(projectId: string): Promise<ProjectCustomData[]> {
  const { data } = await axios({
    method: 'GET',
    url: `/v3/projects/${projectId}/custom-data`,
  });
  return data;
}

export async function addWmtsCustomData(projectId: string, body: AddWmtsCustomDataBody): Promise<ProjectCustomData> {
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/${projectId}/custom-data/wmts`,
    data: body,
  });
  return data;
}

export async function inspectWmtsCapabilities(projectId: string, capabilitiesUrl: string): Promise<WmtsInspectResult> {
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/${projectId}/custom-data/wmts/inspect`,
    data: { capabilitiesUrl },
  });
  return data;
}

// The uploader's per-category color and name picks; ingest owns the authoritative list and
// recomputes `count`, carrying `color` and `label` forward by `value`.
export interface CustomDataCategoryPick {
  value: string;
  count: number;
  color: string;
  label?: string;
}

export async function addShapefileCustomData(
  projectId: string,
  file: File,
  label?: string,
  visibleToCustomer?: boolean,
  categories?: readonly CustomDataCategoryPick[],
): Promise<ProjectCustomData> {
  const form = new FormData();
  form.append('file', file);
  // Server defaults label to the filename (sans .zip), so only send it when set.
  if (label) form.append('label', label);
  // Multipart text fields are strings; the server coerces. Only send when explicitly set.
  if (visibleToCustomer !== undefined) form.append('visibleToCustomer', String(visibleToCustomer));
  if (categories?.length) form.append('categories', JSON.stringify(categories));
  const { data } = await axios({
    method: 'POST',
    url: `/v3/projects/${projectId}/custom-data/shapefile`,
    data: form,
  });
  return data;
}

export async function setCustomDataVisibility(
  projectId: string,
  id: string,
  visibleToCustomer: boolean,
): Promise<ProjectCustomData> {
  const { data } = await axios({
    method: 'PATCH',
    url: `/v3/projects/${projectId}/custom-data/${id}/visibility`,
    data: { visibleToCustomer },
  });
  return data;
}

// Colors and names; the server keeps ingest's counts and geometry types and rejects an unknown value.
export async function setCustomDataCategories(
  projectId: string,
  id: string,
  categories: readonly { value: string; color: string; label?: string }[],
): Promise<ProjectCustomData> {
  const { data } = await axios({
    method: 'PATCH',
    url: `/v3/projects/${projectId}/custom-data/${id}/categories`,
    data: { categories },
  });
  return data;
}

export async function updateCustomDataLabel(projectId: string, id: string, label: string): Promise<ProjectCustomData> {
  const { data } = await axios({
    method: 'PATCH',
    url: `/v3/projects/${projectId}/custom-data/${id}/label`,
    data: { label },
  });
  return data;
}

export async function deleteCustomData(projectId: string, id: string): Promise<void> {
  await axios({
    method: 'DELETE',
    url: `/v3/projects/${projectId}/custom-data/${id}`,
  });
}

export async function getCustomDataSourceDownload(projectId: string, id: string): Promise<DownloadSignedUrlResponse> {
  const { data } = await axios({
    method: 'GET',
    url: `/v3/projects/${projectId}/custom-data/${id}/download`,
  });
  return data;
}
